import * as React from "react";
import clsx from "clsx";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Cpu } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Card } from "../../../ui/components/Card";
import { useAuth } from "../../lib/auth/hooks";
import * as productsApi from "../../lib/api/products";
import type { Component } from "../../lib/api/products";
import { useI18n } from "../../lib/i18n";
import * as s from "./ProductPage.css";

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

function Field({ label, value }: { label: string; value?: string | null }) {
  const { t } = useI18n();
  return (
    <div className={s.fieldGroup}>
      <span className={s.fieldLabel}>{label}</span>
      {value ? (
        <span className={s.fieldValue}>{value}</span>
      ) : (
        <span className={s.fieldValueMuted}>{t("pro.products.notSet")}</span>
      )}
    </div>
  );
}

export function ComponentDetailPage({ componentId }: { componentId: string }) {
  const { t, tx } = useI18n();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");

  const [component, setComponent] = React.useState<Component | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    productsApi
      .getComponentById(token, componentId)
      .then(setComponent)
      .catch((err) => setError(err?.message || "Failed to load component"))
      .finally(() => setLoading(false));
  }, [token, componentId]);

  const handleBack = () => {
    if (component?.productId) {
      navigate({ to: "/workspace/products/$productId", params: { productId: component.productId } });
    } else {
      navigate({ to: "/workspace/products" });
    }
  };

  const handleEdit = () =>
    navigate({ to: "/workspace/components/$componentId/edit", params: { componentId } });

  if (loading) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <Text level="body-medium" style={{ color: "#9CA3AF" }}>{tx("shared.common.loading")}</Text>
        </div>
      </div>
    );
  }

  if (error || !component) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <button className={s.backLink} onClick={handleBack}>
            <ArrowLeft size={14} /> Back
          </button>
          <Callout variant="error">{error ?? "Component not found."}</Callout>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <div className={s.pageColumn}>
        <button className={s.backLink} onClick={handleBack}>
          <ArrowLeft size={14} /> Product
        </button>

        <div className={s.header}>
          <div className={s.headerLeft}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Cpu size={20} />
              <span className={s.componentTypeChip}>{component.componentType}</span>
              <Text level="h2" weight="bold">{component.name ?? component.componentType.toUpperCase()}</Text>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {!component.isActive && (
                <span className={s.componentInactiveBadge} style={{ fontSize: 11 }}>{tx("pro.components.inactive")}</span>
              )}
              {component.componentIndex != null && (
                <span className={s.componentDetailText}>Index {component.componentIndex}</span>
              )}
            </div>
          </div>
          <div className={s.headerActions}>
            <Button type="button" color="primary" variant="outline" onClick={handleEdit}>
              Edit
            </Button>
          </div>
        </div>

        <div className={s.sections}>

          {/* Overview */}
          <Card maxWidth={960} className={s.sectionCard}>
            <div className={s.cardInner}>
              <div className={s.sectionHeader}>
                <Text level="h3" weight="bold">{tx("pro.products.overview")}</Text>
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.components.componentType")} value={component.componentType.toUpperCase()} />
                <Field label={t("pro.components.displayName")} value={component.name} />
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.components.index")} value={component.componentIndex != null ? String(component.componentIndex) : null} />
                <Field label={t("pro.products.status")} value={component.isActive ? "Active" : "Inactive"} />
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.components.iotComponentId")} value={component.iotComponentId} />
              </div>
              {component.dataCatalogName && (
                <div className={s.fieldRow}>
                  <Field label={t("pro.components.dataCatalog")} value={component.dataCatalogName} />
                </div>
              )}
            </div>
          </Card>

          {/* Identifiers */}
          <Card maxWidth={960} className={s.sectionCard}>
            <div className={s.cardInner}>
              <div className={s.sectionHeader}>
                <Text level="h3" weight="bold">{tx("pro.products.identifiers")}</Text>
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.products.serialNumber")} value={component.serial} />
                <Field label={t("pro.products.macAddress")} value={component.macAddress} />
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.components.manufacturer")} value={component.manufacturer} />
                <Field label={t("pro.products.model")} value={component.model} />
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.components.hardwareVersion")} value={component.hwVersion} />
                <Field label={t("pro.components.firmwareVersion")} value={component.fwVersion} />
              </div>
            </div>
          </Card>

          {/* System */}
          <Card maxWidth={960} className={s.sectionCard}>
            <div className={s.cardInner}>
              <div className={s.sectionHeader}>
                <Text level="h3" weight="bold">{tx("pro.products.system")}</Text>
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.components.componentId")} value={component.id} />
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.components.productId")} value={component.productId} />
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.products.created")} value={formatDate(component.createdAt)} />
                <Field label={t("pro.products.updated")} value={formatDate(component.updatedAt)} />
              </div>
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
}
